/**
 * Portable field lists and server-limit mirrors of the table-views lane.
 * Every cap names the Java constant it mirrors.
 */
import { VIEWS_FIELDS } from "@/lib/api/clients/TableViewClient";
import type { StripSteps } from "../kinds/run-helpers";
import type { PortableTableType } from "./types";

export { VIEWS_FIELDS };

export const VIEW_PORTABLE_FIELDS = ["entry", "tableType", "name", "description", "fields"] as const;
export const FILTER_PORTABLE_FIELDS = ["entry", "tableType", "name", "description", "filter", "conditions"] as const;

/**
 * Never portable: identity, owner, timestamps, and the detail-layout links
 * (client UUIDs inside `event.guestDetailLayouts` / `event.detailLayouts` of
 * the SOURCE event — meaningless anywhere else). Pinned by test.
 */
export const ALWAYS_STRIPPED_FIELDS: readonly string[] = [
  "tableViewId",
  "tableFilterId",
  "tableViewType",
  "userId",
  "eventId",
  "createdAt",
  "updatedAt",
  "guestDetailLayoutId",
  "detailLayoutId",
];

/** The two file keys whose presence the summary reports (`warnLayoutLinkDropped`). */
export const LAYOUT_LINK_FIELDS: readonly string[] = ["guestDetailLayoutId", "detailLayoutId"];

export const TABLE_TYPES: readonly PortableTableType[] = ["GUEST", "EMAIL_SENT", "CHECK_IN", "ACTION_LOG", "SPEAKER", "SPONSOR"];

/** `ViewFilterHandling.createView/createFilter` — the cockpit's own caps (the server allows 255 / 1000). */
export const NAME_MAX = 50;
export const DESCRIPTION_MAX = 250;
/** The reserved name of the unsaved ad-hoc view/filter. */
export const RESERVED_NAME = "DEFAULT";

/** `TableViewValidator.MAX_FIELDS`. */
export const VIEW_FIELDS_MAX = 200;
/** `TableViewValidator.KEY_PATTERN` (= `FilterValidator.KEY_PATTERN`). */
export const VIEW_FIELD_KEY_PATTERN = /^[a-zA-Z0-9._-]{1,100}$/;
/** `TableViewValidator.DENY_NAME_SUBSTRINGS` — case-insensitive substring reject. */
export const VIEW_FIELD_DENY_SUBSTRINGS: readonly string[] = [
  "password",
  "passwd",
  "salt",
  "pepper",
  "secret",
  "apikey",
  "api_key",
  "token",
  "otp",
  "creditcard",
  "credit_card",
  "cardnumber",
  "card_number",
  "cvv",
  "cvc",
  "ssn",
];
/** `TableViewValidator.ALLOWED_EXACT_NAMES` — `GuestLoginToken.KEY`. */
export const VIEW_FIELD_ALLOWED_EXACT: ReadonlySet<string> = new Set(["loginToken"]);

/**
 * The `?fields=` projections of the export reads. The views one is the
 * client's own (`VIEWS_FIELDS`); the filters one mirrors the module-private
 * `FILTERS_FIELDS` of `TableFilterClient` — pinned against the source by
 * `projectionPins.test.ts`. Both ⊆ the backend's `ALLOWED_TABLE_*_FIELDS`.
 */
export const VIEW_EXPORT_PROJECTION = VIEWS_FIELDS;
export const FILTER_EXPORT_PROJECTION = "tableFilterId,name,filter,conditions,description,updatedAt";

/** The pre-flight's name reads. */
export const VIEW_NAMES_PROJECTION = "tableViewId,name";
export const FILTER_NAMES_PROJECTION = "tableFilterId,name";

/**
 * Pager bases — pinned by test: the views list is 1-based
 * (`getTableViewsGuests(page = 1)`), the filters list 0-based
 * (`getTableFiltersGuests(page = 0)`). Reading the wrong base skips or
 * repeats the first page silently.
 */
export const VIEWS_FIRST_PAGE = 1;
export const FILTERS_FIRST_PAGE = 0;
export const PAGE_SIZE = 100;
/** Hard wall so a misbehaving endpoint cannot loop. */
export const MAX_PAGES = 50;

/** The server's answer to a name the (event, type) already holds — both spellings the client can see. */
export const NAME_COLLISION_CODES: ReadonlySet<string> = new Set(["nameAlreadyExists", "Name already exists."]);

/**
 * Backend codes whose cause is the condition tree alone (the
 * `GuestQueryConditionValidator` family): the filter is retried without
 * `conditions` — only meaningful when flat rows remain, which the step
 * function checks.
 */
export const CONDITION_CODES: readonly string[] = [
  "conditionTooDeep",
  "filterKeyNotAllowed",
  "filterListTooLong",
  "filterRegexTooLong",
  "filterValueMustBeList",
  "filterValueTooLong",
  "invalidFilterOperator",
  "operatorNotAllowedInQueryContext",
  "tooManyFilters",
  "conditionsNotSupportedForEventFilters",
];

/** A filter without its tree — `null` when nothing flat would remain (give up). */
function withoutConditions(payload: Record<string, unknown>): Record<string, unknown> | null {
  if (payload.conditions === undefined) return null;
  const flat = payload.filter;
  if (!Array.isArray(flat) || flat.length === 0) return null;
  const { conditions: _dropped, ...rest } = payload;
  void _dropped;
  return rest;
}

export const FILTER_STRIP_STEPS: StripSteps = Object.fromEntries(CONDITION_CODES.map((code) => [code, [withoutConditions]]));

/** The name a function step reports in `stripped` (the function's own name). */
export const CONDITIONS_STRIP_NAME = withoutConditions.name;

/** 429 handling of `createWithRecovery`. */
export const RATE_LIMIT_MAX_RETRIES = 3;
export const RATE_LIMIT_DEFAULT_WAIT_MS = 2_000;
